import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { MobileDevice } from '../../../../../types';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { Dispatch, SetStateAction } from 'react';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { UseMutateFunction } from '@tanstack/react-query';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

interface MobileManagementToolbarProps {
  selectedDevices: MobileDevice[];
  toolsRoles: string[];
  setOpenDeleteDevice: Dispatch<SetStateAction<boolean>>;
  setOpenApproveDevice: Dispatch<SetStateAction<boolean>>;
  setOpenBlockDevice: Dispatch<SetStateAction<boolean>>;
  setOpenWipeDevice: Dispatch<SetStateAction<boolean>>;
  setOpenWipeAccountDevice: Dispatch<SetStateAction<boolean>>;
  handleExportMutate?: UseMutateFunction<string, Error, { changeLog: string }>;
  isExporting?: boolean;
  setOpenExportChangeLog?: Dispatch<SetStateAction<boolean>>;
  changeLogRoles?: string[];
}

export default function MobileManagementToolbar({
  selectedDevices,
  toolsRoles,
  setOpenDeleteDevice,
  setOpenApproveDevice,
  setOpenBlockDevice,
  setOpenWipeDevice,
  setOpenWipeAccountDevice,
  handleExportMutate,
  isExporting,
  setOpenExportChangeLog,
  changeLogRoles,
}: MobileManagementToolbarProps) {
  const queryClient = useQueryClient();

  const onExport = () => {
    if (handleExportMutate && setOpenExportChangeLog) {
      if (
        changeLogRoles &&
        isAbleToSeeUIElement(
          changeLogRoles,
          ToolsRoles.CanExportMobileDevices.value
        )
      ) {
        setOpenExportChangeLog(true);
      } else {
        handleExportMutate({
          changeLog: '',
        });
      }
    }
  };

  // All
  const hasNoLength = selectedDevices.length === 0;

  // Approve
  const hasOnlyPendingOrBlockedDevices = selectedDevices.every(
    (device) => device.status === 'PENDING' || device.status === 'BLOCKED'
  );
  const hasApprovePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanApproveMobile.value
  );

  // Block
  const hasOnlyPendingOrApprovedDevices = selectedDevices.every(
    (device) => device.status === 'PENDING' || device.status === 'APPROVED'
  );
  const hasBlockPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanBlockMobile.value
  );

  // Remote Wipe
  const hasOnlyPendingOrApprovedOrBlockedCompanyDevices = selectedDevices.every(
    (device) =>
      (device.status === 'PENDING' ||
        device.status === 'APPROVED' ||
        device.status === 'BLOCKED') &&
      device.isForCompanyUseOnly
  );
  const hasWipePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanRemoteWipeMobile.value
  );

  // Remote Account Wipe
  const hasOnlyPendingOrApprovedOrBlockedDevices = selectedDevices.every(
    (device) =>
      device.status === 'PENDING' ||
      device.status === 'APPROVED' ||
      device.status === 'BLOCKED'
  );
  const hasRemoteAccountWipePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanRemoteWipeAccountMobile.value
  );

  // Delete
  const hasDeletePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanDeleteMobile.value
  );

  const hasExportPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanExportMobileDevices.value
  );

  return (
    <SharedToolbar
      secondaryButtons={
        <>
          <SharedIconButton
            variant="enable"
            onClick={() => {
              setOpenApproveDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrBlockedDevices ||
              !hasApprovePermission
            }
            tooltipLabel="Approve"
          />
          <SharedIconButton
            variant="block"
            onClick={() => {
              setOpenBlockDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedDevices ||
              !hasBlockPermission
            }
            tooltipLabel="Block"
          />
          <SharedIconButton
            variant="sweep"
            onClick={() => {
              setOpenWipeDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedOrBlockedCompanyDevices ||
              !hasWipePermission
            }
            tooltipLabel="Remote Wipe"
          />
          <SharedIconButton
            variant="folderDelete"
            onClick={() => {
              setOpenWipeAccountDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedOrBlockedDevices ||
              !hasRemoteAccountWipePermission
            }
            tooltipLabel="Remote Account Wipe"
          />
          <SharedIconButton
            variant="delete"
            onClick={() => {
              setOpenDeleteDevice(true);
            }}
            disabled={hasNoLength || !hasDeletePermission}
            tooltipLabel="Delete"
          />
          {handleExportMutate && (
            <SharedIconButton
              variant="export"
              onClick={() => onExport()}
              disabled={!hasExportPermission || isExporting}
              isLoading={isExporting}
              tooltipLabel="Export Results"
            />
          )}
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.Devices],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="mobileManagementGridStates"
            module={ModuleList.modules.devices.name}
            section={ModuleList.modules.devices.sections.mobileManagement}
          />
        </>
      }
    />
  );
}
